import { ref, shallowRef, computed, onBeforeUnmount, nextTick } from 'vue';
import axios from 'axios';
import { isDocumentResult } from '@/utils/documentResultValidator';

export function useDocumentCreation(config) {
  const self = {
    documentCreationRequests: shallowRef(new Map()),
    activeDocumentPolling: shallowRef(new Map()),
    showDocumentCreationStatus: ref(false),
  };

  self.getDocInputsForRow = (request, row, index) => {
    if (row.row_id && request.docInputsByRowIdx?.[row.row_id]) {
      return request.docInputsByRowIdx[row.row_id];
    }
    return request.docInputsByIndex?.[index] || { manual: {}, hierarchies: [] };
  };

  async function pollDocumentCreationStatus(requestId, notify) {
    try {
      const response = await axios.get(`/document/docCreationRequestState/${requestId}`);

      if (response.data && response.status === 200) {
        const statusData = response.data;
        const request = self.documentCreationRequests.value.get(requestId);

        if (!request) return;

        request.status = statusData.status || 'processing';
        request.progress = statusData.progress || null;
        request.message = statusData.message || '';
        request.lastUpdated = new Date();

        if (statusData.timestamp) {
          request.timestamp = statusData.timestamp;
        }

        if (statusData.row_start !== undefined && statusData.row_end !== undefined) {
          request.rowStart = statusData.row_start;
          request.rowEnd = statusData.row_end;
        }

        if (statusData.rows) {
          request.rows = statusData.rows;
        }

        if (statusData.results) {
          request.results = statusData.results;
        }

        self.documentCreationRequests.value = new Map(self.documentCreationRequests.value);

        if (statusData.status === 'Complete' || statusData.status === 'Error') {
          self.stopDocumentCreationPolling(requestId);

          if (statusData.status === 'Complete') {
            const totalItems = request.applicationData?.reduce((sum, row) => sum + row.selectedCount, 0) ||
                             statusData.rows?.length || 0;
            notify(`Document creation completed successfully (${totalItems} items processed)`, 'success');
          } else {
            notify(`Document creation failed: ${statusData.message || 'Unknown error'}`, 'error');
          }
        }
      } else {
        console.error('Failed to get document creation status:', response.data);
      }
    } catch (error) {
      console.error('Error polling document creation status:', error);
    }
  }

  self.startDocumentCreationPolling = (requestId, notify) => {
    if (self.activeDocumentPolling.value.has(requestId)) {
      return;
    }

    self.showDocumentCreationStatus.value = true;

    const intervalId = setInterval(() => {
      pollDocumentCreationStatus(requestId, notify);
    }, 2000);

    self.activeDocumentPolling.value.set(requestId, intervalId);

    pollDocumentCreationStatus(requestId, notify);
  };

  self.stopDocumentCreationPolling = (requestId) => {
    const intervalId = self.activeDocumentPolling.value.get(requestId);
    if (intervalId) {
      clearInterval(intervalId);
      self.activeDocumentPolling.value.delete(requestId);
    }
  };

  self.stopAllDocumentCreationPolling = () => {
    for (const [, intervalId] of self.activeDocumentPolling.value) {
      clearInterval(intervalId);
    }
    self.activeDocumentPolling.value.clear();
  };

  self.createDocumentCreationRequest = async (applicationData, nodeData, promptChain, doc, notify) => {
    try {
      if (!nodeData || typeof nodeData !== 'object' || typeof nodeData === 'function') {
        console.error('Invalid nodeData:', nodeData);
        notify('Configuration error: node data not available', 'error');
        return;
      }

      const payload = {
        docs: applicationData,
        source: 'ai_content_generation',
        prompt_chain_id: promptChain?.prompt_chain_id,
        node: nodeData,
        doc: doc,
        tmpl_id: nodeData.tmpl_id,
        docType: promptChain?.docType || nodeData.docType,
        row_end: applicationData.length,
        row_start: 1,
      };

      const response = await axios.post('/document/docCreationRequest', payload);

      if (response.data && response.data.Status === 'OK') {
        const requestId = response.data.Data[0].doc_creation_request_id;

        const allLevelValues = [...new Set(
          applicationData.flatMap(row => row.levelValues || [])
        )];

        const docInputsByRowIdx = {};
        applicationData.forEach(row => {
          docInputsByRowIdx[row.rowIdx] = {
            manual: row.manualInputs || {},
            hierarchies: row.levelValues || []
          };
        });

        const docInputsByIndex = [];
        applicationData.forEach(row => {
          const manualInputs = row.manualInputs || {};
          const hierarchyValues = row.levelValues || [];
          (row.populatedData || []).forEach(() => {
            docInputsByIndex.push({
              manual: manualInputs,
              hierarchies: hierarchyValues
            });
          });
        });

        self.documentCreationRequests.value.set(requestId, {
          id: requestId,
          status: 'queued',
          message: response.data.Message,
          applicationData: applicationData,
          chainName: promptChain?.chain_name || '',
          levelValues: allLevelValues,
          docInputsByRowIdx: docInputsByRowIdx,
          docInputsByIndex: docInputsByIndex,
          createdAt: new Date(),
          progress: null,
          results: null
        });

        await nextTick();

        self.startDocumentCreationPolling(requestId, notify);

        const totalItems = applicationData.reduce((sum, row) => sum + row.selectedCount, 0);
        notify(`Document creation request queued (${totalItems} items from ${applicationData.length} rows)`, 'info');
      } else {
        throw new Error(response.data?.Message || 'Failed to create document creation request');
      }
    } catch (error) {
      console.error('Error creating document creation request:', error);
      throw error;
    }
  };

  self.applySelectedBulkDocumentItems = async (executionResults, docSelection, nodeData, promptChain, doc, notify) => {
    const selectedResults = executionResults.value.filter(result =>
      isDocumentResult(result) && docSelection.selectedResultRows.value.has(result.rowId)
    );

    if (selectedResults.length === 0) {
      notify('Please select at least one row to apply', 'warning');
      return false;
    }

    const validResults = selectedResults.filter(result =>
      docSelection.getSelectedDocumentCount(result.rowId) > 0
    );

    if (validResults.length === 0) {
      notify('Please select at least one document item from the selected rows', 'warning');
      return false;
    }

    try {
      const bulkApplicationData = validResults.map((result, idx) => {
        const selectedItems = docSelection.getSelectedDocumentItems(result.rowId);

        const manualInputs = result.inputs?.manual || {};
        const inputHierarchies = result.inputs?.hierarchies || {};
        const levelValues = Object.values(inputHierarchies).filter(v => v);

        const selectedDocumentData = Array.from(selectedItems).map(index => ({
          ...result.result.populatedData[index],
          _manualInputs: manualInputs,
          _hierarchyValues: levelValues
        }));

        return {
          rowIdx: idx + 1,
          populatedData: selectedDocumentData,
          hierarchies: result.result.hierarchies || [],
          inputHierarchies: inputHierarchies,
          manualInputs: manualInputs,
          levelValues: levelValues,
          tmpl_id: nodeData.tmpl_id,
          docType: promptChain?.docType || nodeData.docType,
          selectedCount: selectedDocumentData.length
        };
      });

      await self.createDocumentCreationRequest(bulkApplicationData, nodeData, promptChain, doc, notify);

      validResults.forEach(result => {
        docSelection.clearAllDocumentItems(result.rowId);
      });
      docSelection.clearAllResultRowSelections();

      return true;
    } catch (error) {
      console.error('Error applying bulk document items:', error);
      notify('Error applying bulk document items: ' + error.message, 'error');
      return false;
    }
  };

  self.activeDocumentCreationRequests = computed(() => {
    return Array.from(self.documentCreationRequests.value.values())
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  });

  self.getDocCreationStats = computed(() => {
    let processing = 0;
    let complete = 0;
    let failed = 0;

    self.activeDocumentCreationRequests.value.forEach(request => {
      if (request.rows) {
        request.rows.forEach(row => {
          if (row.state === 'Processing' || row.state === 'queued') {
            processing++;
          } else if (row.state === 'Complete') {
            complete++;
          } else if (row.state === 'Error' || row.state === 'Failed') {
            failed++;
          }
        });
      }
    });

    return { processing, complete, failed };
  });

  onBeforeUnmount(() => {
    self.stopAllDocumentCreationPolling();
  });

  return self;
}
